import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePagination } from "../../../../hooks/usePagination";
import api from "../../../../util/api";
import {
  SupplierApiType,
  PaginatedResponse,
  QueryKeys,
} from "../../../../util/types";

export interface SuppliersFilters {
  from?: string;
  to?: string;
  companyId?: string;
  searchKeyword?: string | null;
  createdBy?: string;
}

export const useGetSuppliers = (
  filters: SuppliersFilters = {},
  sort = { field: "createdAt", direction: "desc" },
  ownerId?: string
) => {
  const { page, limit, onPaginationChange, resetPageNum } = usePagination();

  const {
    data,
    isLoading,
    isFetching,
    refetch: refreshSuppliers,
  } = useQuery<PaginatedResponse<SupplierApiType>>({
    queryKey: [
      QueryKeys.LIST_SUPPLIERS,
      page,
      limit,
      filters.searchKeyword ?? "",
      filters.from ?? "",
      filters.to ?? "",
      filters.createdBy ?? "",
      filters.companyId ?? "",
      sort.field,
      sort.direction,
      ownerId ?? "",
    ],
    queryFn: async () => {
      const { companyId, ...rest } = filters;
      return await api.getSuppliers(
        page,
        limit,
        {
          sort: sort.field,
          sortDirection: sort.direction,
          ...rest,
        },
        companyId ?? ownerId
      );
    },
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    resetPageNum();
  }, [JSON.stringify(filters), ownerId]);

  // Reset page if current page is beyond available pages after filtering
  useEffect(() => {
    if (data?.totalDocs !== undefined && page > 1) {
      const maxPage = Math.ceil(data.totalDocs / limit);
      if (maxPage > 0 && page > maxPage) {
        resetPageNum();
      }
    }
  }, [data?.totalDocs, page, limit, resetPageNum]);

  return {
    suppliers: data?.docs ?? [],
    totalDocs: data?.totalDocs ?? 0,
    loading: isLoading || isFetching,
    refreshSuppliers,
    page,
    limit,
    onPaginationChange,
  };
};
